<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import { GRAFICO_DO_TURNO } from '@modules/aawz-intelligence-v2/constants/Text.busca'
import { apiArtefatos } from '@modules/aawz-intelligence-v2/api/apiArtefatos'
import { downloadBase64 } from '@utils/Download'

/**
 * [spec-003 T301 / US-5] O gráfico da análise, INLINE na conversa.
 *
 * "WHEN a análise produz gráfico THE system SHALL exibi-lo inline no chat como
 * PNG e disponibilizá-lo também como artefato baixável."
 *
 * ★ Era a única das três user stories abertas de verdade: planilha (US-4) e
 * documento (US-6) já baixavam pelo bloco de Entregas (spec-008 T028), e o
 * único gráfico do módulo — `ia-insight-card/ia-sparkline.vue` — é de PAINEL,
 * desligado do run.
 *
 * ⚠️ A entrega é METADADO; os bytes só chegam em base64, por
 * `apiArtefatos.baixar`. Para DESENHAR, eles têm de vir antes do clique — é o
 * único ponto do thread em que o download acontece sozinho. Por isso o teto é
 * estreito: só entregas que JÁ são gráfico (`graficosEntreEntregas`), uma
 * requisição por objeto, e nenhuma repetição (a presença da chave em
 * `porObjeto` é o que marca "já pedi este").
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
export default {
  name: 'IntelligenceV2GraficoDoTurno',
  props: {
    /**
     * As entregas do turno que são gráfico, já filtradas por
     * `utils/tipo-de-artefato.js::graficosEntreEntregas` — shape do composer
     * (`{id_objeto, name, type}`).
     *
     * ⚠️ `Array` com `default` de função: prop `Array` recebendo `null`
     * EXPLÍCITO não cai no default do Vue 2 (só `undefined` cai).
     */
    graficos: {
      type: Array,
      default: function () {
        return []
      },
    },
    /** A conversa dona dos objetos — metade do par que a rota de download pede. */
    idConversa: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      MOLDURA_DO_THREAD,
      GRAFICO_DO_TURNO,
      // `{ [id_objeto]: {carregando, dataUrl, base64, name, extension, falhou} }`
      // — objeto NOVO a cada mudança: o Vue 2 não detecta chave adicionada num
      // objeto reativo, e `$set` é o que o CLAUDE.md do repo desaconselha por
      // ser fácil de esquecer.
      porObjeto: {},
    }
  },
  computed: {
    // Só entra na tela o que JÁ tem desfecho — carregando, desenhado ou falho.
    // A entrega ainda não pedida não desenha moldura nenhuma.
    itens() {
      const estado = this.porObjeto
      return this.graficos
        .filter(function (grafico) {
          return Boolean(estado[grafico.id_objeto])
        })
        .map(function (grafico) {
          const atual = estado[grafico.id_objeto]
          return {
            chave: grafico.id_objeto,
            nome: grafico.name || GRAFICO_DO_TURNO.ALT_PADRAO,
            carregando: Boolean(atual.carregando),
            // ★ O discriminante do "renderiza a imagem": a URL existe SÓ quando
            // o envelope veio 200 E trouxe bytes. Sem ela nenhuma `<img>` é
            // desenhada — moldura quebrada afirmaria que houve gráfico.
            dataUrl: atual.dataUrl || '',
            falhou: Boolean(atual.falhou),
            rotuloBaixar: GRAFICO_DO_TURNO.BAIXAR(grafico.name || GRAFICO_DO_TURNO.ALT_PADRAO),
          }
        })
    },
    temAlgoParaMostrar() {
      return this.itens.length > 0
    },
  },
  watch: {
    // Os gráficos chegam com a listagem de entregas, DEPOIS do `mounted` — o
    // `immediate` cobre o caso em que já estavam lá na montagem.
    graficos: {
      immediate: true,
      handler() {
        this.buscarOsQueFaltam()
      },
    },
    idConversa() {
      this.buscarOsQueFaltam()
    },
  },
  methods: {
    buscarOsQueFaltam() {
      if (!this.idConversa) return
      this.graficos.forEach((grafico) => {
        if (!grafico || !grafico.id_objeto) return
        if (this.porObjeto[grafico.id_objeto]) return
        this.buscarBytes(grafico)
      })
    },
    /**
     * Pede os bytes de UM gráfico.
     *
     * ⚠️ 403/404 chegam com HTTP 200 e o status no CORPO (contrato da
     * plataforma): o desfecho é lido de `r.status`, nunca de um `catch` — este
     * só pegaria exceção de JS. E o guard tem DUAS partes: sem `status === 200`
     * um corpo junto de uma recusa seria desenhado; sem `value.base64` a `<img>`
     * nasceria com `src` vazio, que é a moldura quebrada.
     */
    buscarBytes(grafico) {
      const id = grafico.id_objeto
      this.marcar(id, { carregando: true })
      return apiArtefatos
        .baixar({ id_conversa: this.idConversa, id_objeto: id })
        .then((r) => this.guardarDesfecho(id, grafico, r))
    },
    guardarDesfecho(id, grafico, r) {
      const valor = (r && r.value) || null
      if (r && r.status === 200 && valor && valor.base64) {
        const contentType = grafico.type || 'image/png'
        this.marcar(id, {
          carregando: false,
          dataUrl: 'data:' + contentType + ';base64,' + valor.base64,
          base64: valor.base64,
          name: valor.name,
          extension: valor.extension,
        })
        return
      }
      // Nenhum snack aqui: o gráfico é um EXTRA da resposta, e um aviso
      // flutuante por artefato que não desenhou seria ruído sobre um turno que
      // entregou os números. Quem conta o que houve é a frase no lugar da
      // imagem (`NAO_SAIU`), que fica.
      this.marcar(id, { carregando: false, falhou: true })
    },
    marcar(id, estado) {
      const mapa = { ...this.porObjeto }
      mapa[id] = { ...(mapa[id] || {}), ...estado }
      this.porObjeto = mapa
    },
    /**
     * Baixa o gráfico que já está na tela.
     *
     * ★ Sem segunda requisição: os bytes vieram para DESENHAR e continuam em
     * memória. Pedi-los de novo dobraria o tráfego do maior payload do turno.
     */
    baixar(chave) {
      const item = this.porObjeto[chave]
      if (!item || !item.base64) return
      downloadBase64(item.base64, item.name, item.extension)
    },
  },
}
</script>

<template>
  <div
    v-if="temAlgoParaMostrar"
    class="grafico-do-turno ia-block-card"
    data-test="grafico-do-turno"
    v-bind="MOLDURA_DO_THREAD"
  >
    <div class="ia-block-card__cabecalho ia-block-card__cabecalho--fita">
      <icon-svg
        name="chart-bar"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">{{ GRAFICO_DO_TURNO.TITULO }}</span>
    </div>

    <div class="grafico-do-turno__corpo">
      <div
        v-for="item in itens"
        :key="item.chave"
        class="grafico-do-turno__item"
        data-test="grafico-do-turno-item"
      >
        <p v-if="item.carregando" class="grafico-do-turno__estado ia-texto-auxiliar">
          {{ GRAFICO_DO_TURNO.CARREGANDO }}
        </p>

        <!-- ★ `alt` com o NOME do arquivo, nunca vazio: sem ele, quem usa
             leitor de tela perde o único sinal de que a análise produziu um
             gráfico (regra de acessibilidade do CLAUDE.md do repo). -->
        <img
          v-if="item.dataUrl"
          :src="item.dataUrl"
          :alt="item.nome"
          class="grafico-do-turno__imagem"
          data-test="grafico-do-turno-imagem"
        />

        <div v-if="item.dataUrl" class="grafico-do-turno__acoes">
          <BaseButton
            underlined
            :aria-label="item.rotuloBaixar"
            data-test="grafico-do-turno-baixar"
            @click="baixar(item.chave)"
          >
            {{ item.rotuloBaixar }}
          </BaseButton>
        </div>

        <!-- "IF a renderização falha THEN entregar a tabela e dizer que o
             gráfico não saiu" — a frase NOMEIA o que sobrou (os números na
             resposta, o arquivo em Entregas). `role="alert"`: é uma afirmação
             sobre o que NÃO entrou na tela. -->
        <p
          v-if="item.falhou"
          class="grafico-do-turno__estado ia-texto-auxiliar grafico-do-turno__estado--falha"
          role="alert"
          data-test="grafico-do-turno-falha"
        >
          {{ GRAFICO_DO_TURNO.NAO_SAIU }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.grafico-do-turno
  margin-top: 12px

.ia-block-card__cabecalho--fita
  border-bottom: 1px solid var(--border-hairline)

.grafico-do-turno__corpo
  display: flex
  flex-direction: column
  gap: 12px
  padding: 12px 14px

.grafico-do-turno__item
  display: flex
  flex-direction: column
  gap: 6px
  min-width: 0

.grafico-do-turno__imagem
  display: block
  max-width: 100%
  // A altura acompanha a largura da bolha; um gráfico alto demais empurraria o
  // texto da resposta para fora da dobra.
  height: auto
  border-radius: var(--radius-sm)
  border: 1px solid var(--border-hairline)

.grafico-do-turno__acoes
  display: flex
  justify-content: flex-end

.grafico-do-turno__estado
  color: var(--neutral-700)
  margin: 0

.grafico-do-turno__estado--falha
  color: var(--danger-text)
</style>
